import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { useTranslation } from "next-i18next"
import CartItems from "@/components/cart/CartItems/"
import { showToast, selectGenericState } from "@/store/features/genericSlice"

import CartInfo from "@/components/cart/CartInfo"
import OrderSummary from "@/components/cart/OrderSummary/OrderSummary"
import {
  calculateCartData,
  getCurrency,
  recalculateCartRoomInfo,
  getShortenedUrlPath,
  getLocationByCookies,
} from "@/utils/helper"

import LookUpCart from "@/components/cart/LookUpCart"

import {
  discount,
  getCart,
  selectCartState,
  shareCart,
  shipmentUpdate,
  taxCalculation,
  addToCart,
  shipmentCart,
  createCart,
  setCardStatus,
  setQtyLoading,
} from "@/store/features/cartSlice"

import PageLoader from "@/components/core/PageLoader/PageLoader"
import { getTranslations } from "@/components/cart/localization"
import { getStock } from "@/utils/cart"
import { getCartThresholdData } from "@/services/cartThreshold"

import usePrevious from "@/hooks/usePrevious"

import { selectAuthState } from "@/store/features/authSlice"
import {
  getProfileData,
  getShippingMethods,
} from "@/components/cart/cartHelper"
import { getConfig } from "@/constants/config"
import { analyticsCB } from "@/components/cart/cartAnalytics"

const CartLanding = props => {
  const { data: { cartCustomerContactSupport = "", presetConfigs = "" } = {} } =
    props ?? {}

  const { t } = useTranslation("common")

  const {
    cart: activeCart = {},
    status,
    promoError,
    noShippingItemsInCheckout,
    shippingItemstoCheckout,
    isCartLoading = false,
  } = useSelector(selectCartState)

  const {
    access_token: accessToken,
    isAuth,
    user: { firstName = "", koc = "" } = {},
  } = useSelector(selectAuthState)

  const isIscUser = Boolean(koc)

  const prevToken = usePrevious({ accessToken })
  const staticText = getTranslations(t)
  const [showAlert, setShowAlert] = useState(false)

  const [cartData, setCartData] = useState({})
  const [cartDataStatus, setCartDataStatus] = useState(false)

  const dispatch = useDispatch()
  const [globalConfig, setGlobalConfig] = useState({})

  const {
    globalConfig: {
      internationalization: { currencySign = "" } = {},
      general: { checkoutPagePath = "" } = {},
    } = {},
  } = useSelector(selectGenericState)

  const checkoutUrl = getShortenedUrlPath(checkoutPagePath)

  const { key: cartKey = "" } = activeCart

  useEffect(() => {
    if (cartData || noShippingItemsInCheckout) {
      setShowAlert(
        cartData?.outOfStockItems?.length > 0 ||
          noShippingItemsInCheckout.length > 0
      )
    }
  }, [cartData, noShippingItemsInCheckout])

  useEffect(() => {
    const chatElement = document.getElementsByTagName("mavenoid-assistant")
    if (chatElement.length) {
      chatElement.item(0).style.display = "none"
    }
  }, [])

  const initialCartLoad = () => {
    if (!isCartLoading) {
      dispatch(setCardStatus(true))
      dispatch(getCart())
        .unwrap()
        .then((rsp = {}) => {
          const { cartExists = "", error = "" } = rsp
          if (cartExists === "false" && error === "No active cart exists.") {
            getCurrency().then(currency => {
              const cartPayload = {
                currency: currency,
                taxMode: "ExternalAmount",
              }
              dispatch(createCart(cartPayload))
                .unwrap()
                .then(() => {
                  dispatch(setCardStatus(false))
                })
            })
          } else if (rsp?.lineItems?.length) {
            dispatch(setCardStatus(false))
            const payload = {
              actions: [
                {
                  action: "recalculate",
                  updateProductData: true,
                },
              ],
            }
            dispatch(addToCart(payload))
              .unwrap()
              .then(rsp => {
                getZipCode(rsp)
              })
          } else {
            dispatch(setCardStatus(false))
          }
        })
    }
  }

  useEffect(() => {
    if (prevToken && prevToken?.accessToken !== accessToken) {
      initialCartLoad()
    }
  }, [accessToken])

  useEffect(() => {
    dispatch(setCardStatus(false))
    initialCartLoad()

    getConfig().then(async config => {
      setGlobalConfig(config)
    })
  }, [])

  useEffect(() => {
    if (activeCart?.id) {
      const cartData = {
        cart: activeCart,
        shippingItemstoCheckout,
        noShippingItems: noShippingItemsInCheckout,
        quantityUpdatedFlag: window?.pageObj?.updatedSku
          ? window?.pageObj?.updatedSku
          : false,
      }

      calculateCartData(cartData, true).then(rsp => {
        analyticsCB("success", rsp.cart)
        setCartData(rsp)
      })

      getCartThresholdData(activeCart.id).then(thresholdData => {
        if (
          thresholdData &&
          window.adobeDataLayer &&
          window.adobeDataLayer.length > 0
        ) {
          window.adobeDataLayer
            .filter(e => e.event === "cmp:show" && e.page?.cart)
            .forEach(e => {
              e.page.cart.thresholds = thresholdData
            })
        }
      })

      setCartDataStatus(true)
    }
  }, [activeCart, shippingItemstoCheckout, noShippingItemsInCheckout])

  const dispatchCallbackHandler = (type, payload = null) => {
    switch (type) {
      case "discount": {
        return dispatch(discount(payload))
      }
      case "taxCalculation": {
        return dispatch(taxCalculation())
      }
      case "shipmentUpdate": {
        return dispatch(shipmentUpdate(payload))
      }
      case "shareCart": {
        return dispatch(shareCart(payload))
      }
      case "addToCart": {
        return dispatch(addToCart(payload))
      }
      case "shipmentcart": {
        return dispatch(shipmentCart())
      }
    }
  }

  const getZipCode = async payload => {
    const { postalCode, state, country } = payload?.shippingAddress ?? {}

    if (postalCode && state && country) {
      const shippingZip = localStorage.getItem("shippingZip")
      if (!shippingZip) {
        localStorage.setItem("shippingZip", postalCode)
      }

      getShippingMethods()
      return
    }
    let zip = localStorage.getItem("shippingZip")
    if (!zip && isAuth && !isIscUser) {
      const profile = await getProfileData(staticText.profileFailure)
      if (profile?.addresses) {
        const defAddr = profile.defaultShippingAddressId
          ? profile.addresses.find(
              item => item.id === profile.defaultShippingAddressId
            )
          : profile.addresses[0]
        zip = defAddr?.postalCode
      }
    }
    if (!zip) {
      const res = await getLocationByCookies()
      zip = res.zipcode ?? ""
    }

    if (zip) localStorage.setItem("shippingZip", zip)
  }

  const showToastHandler = msg => {
    dispatch(
      showToast({
        message: msg,
        isVisible: true,
      })
    )
  }

  const handleRoomInfoUpdate = (sku, quantity, additionalRoomInfo) => {
    const lineItemId = activeCart.lineItems.find(
      item => item.variant.sku === sku
    ).id
    if (quantity > 0) {
      setQtyLoading(true)
      getStock(sku).then(({ stock = 0 }) => {
        if (Object.keys(additionalRoomInfo).length > 0) {
          quantity <= stock &&
            recalculateCartRoomInfo(
              lineItemId,
              additionalRoomInfo,
              setQtyLoading
            )
        }
      })
    }
  }

  return (
    <>
      <PageLoader loading={status === "loading"} />
      <div className="root container responsivegrid" id="main-content">
        <div id="container-181c79087e" className="cmp-container">
          <div className="container responsivegrid">
            <div id="container-9241f43c45" className="cmp-container">
              {isIscUser ? <LookUpCart /> : null}
              <div className="cart-landing">
                <div className="container kf-react-container cart-landing">
                  <div className="row no-gutters row-space-between">
                    <div
                      className={`${
                        isIscUser || cartData?.cartItems?.length > 0
                          ? "col-lg-9 cart-item-list-section"
                          : "col-lg-12"
                      } ml-full-width `}
                    >
                      <CartItems
                        page="cart"
                        loading={status === "loading"}
                        i18n={staticText}
                        cartItems={cartData?.cartItems}
                        dispatchCallback={dispatchCallbackHandler}
                        estimatedTotal={cartData?.estimatedTotal}
                        cartId={activeCart?.id}
                        cartKey={cartKey}
                        isIscUser={isIscUser}
                        currencySign={currencySign}
                        disableCheckout={
                          cartData?.cartItems?.length === 0 || showAlert
                        }
                        showAlert={showAlert}
                        specFiles={cartData?.specFiles}
                        isAuth={isAuth}
                        username={firstName}
                        handleRoomInfoUpdate={handleRoomInfoUpdate}
                        isCartPage
                        cartDataStatus={cartDataStatus}
                        presetConfigs={presetConfigs}
                      />
                    </div>
                    {isIscUser || cartData?.cartItems?.length > 0 ? (
                      <div className="ml-full-width order-summary-cart">
                        <OrderSummary
                          i18n={staticText}
                          promoError={promoError}
                          status={status}
                          toastError={showToastHandler}
                          dispatchCallback={dispatchCallbackHandler}
                          cartId={activeCart?.id}
                          disableCheckout={cartData?.cartItems?.length <= 0}
                          checkoutUrl={checkoutUrl}
                          currencySign={currencySign}
                          {...cartData}
                        />
                      </div>
                    ) : null}
                  </div>
                  {cartData?.cartItems?.length > 0 ? (
                    <CartInfo
                      i18n={staticText}
                      supportLine={cartCustomerContactSupport}
                      globalConfig={globalConfig}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default CartLanding
